import React, { useState, useEffect, useCallback } from 'react';
import {
  BadgeDollarSign,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  FileText,
  LoaderCircle,
  Pencil,
  Plus,
  RefreshCw,
  SlidersHorizontal,
  WalletCards,
  XCircle,
} from 'lucide-react';
import { PlanService, CreatePlanRequest, UpdatePlanRequest } from '../services/planService';
import { Plan } from '../models/Plan';
import { formatDateString } from '../lib/utils';
import { CreatePlanModal } from './CreatePlanModal';
import { EditPlanModal } from './EditPlanModal';

export const PaymentPlans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState<boolean>(false);
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [verificationFee, setVerificationFee] = useState<number | null>(null);
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const activeFilterCount = activeOnly ? 1 : 0;

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await PlanService.getPlans(activeOnly);
      setPlans(response.data.plans);
      setVerificationFee(response.data.verificationFee ?? null);
      setDefaultCurrency(response.data.currency || 'USD');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch plans');
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const formatCurrency = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const formatValidity = (validity: number) => {
    if (validity === 1) {
      return '1 month';
    }
    return `${validity} months`;
  };

  const handleCreatePlan = async (planData: CreatePlanRequest) => {
    try {
      setIsCreating(true);
      setError(null);
      await PlanService.createPlan(planData);
      // Refresh the plans list after successful creation
      await fetchPlans();
      setIsCreateModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create plan';
      setError(errorMessage);
      throw err; // Re-throw to let the modal handle it
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdatePlan = async (planId: string, updateData: UpdatePlanRequest) => {
    try {
      setIsUpdating(true);
      setError(null);
      await PlanService.updatePlan(planId, updateData);
      // Refresh the plans list after successful update
      await fetchPlans();
      setIsEditModalOpen(false);
      setSelectedPlan(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update plan';
      setError(errorMessage);
      throw err; // Re-throw to let the modal handle it
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditClick = (plan: Plan) => {
    setSelectedPlan(plan);
    setError(null);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-2xl bg-slate-950 px-5 py-6 text-white shadow-sm sm:px-7 sm:py-7" aria-labelledby="payment-plans-title">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_center,_rgba(59,130,246,0.22),_transparent_68%)]" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-blue-200">
              <WalletCards className="h-3.5 w-3.5" aria-hidden="true" />
              Subscription catalog
            </div>
            <h1 id="payment-plans-title" className="text-2xl font-semibold tracking-tight sm:text-3xl">Payment plans</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Create and maintain subscription options available to service providers.</p>
          </div>
          <button type="button" onClick={() => { setError(null); setIsCreateModalOpen(true); }} className="inline-flex h-11 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Create plan
          </button>
        </div>
      </section>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" role="list" aria-label="Payment plan summary">
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><FileText className="h-5 w-5" aria-hidden="true" /></span><div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Total plans</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : plans.length.toLocaleString()}</p><p className="mt-1 text-xs text-slate-500">Currently loaded catalog</p></div></div>
        </article>
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><BadgeDollarSign className="h-5 w-5" aria-hidden="true" /></span><div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Verification fee</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : verificationFee === null ? '—' : formatCurrency(verificationFee, defaultCurrency)}</p><p className="mt-1 text-xs text-slate-500">Backend configured default</p></div></div>
        </article>
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-5 w-5" aria-hidden="true" /></span><div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Active plans</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : plans.filter(plan => plan.isActive).length.toLocaleString()}</p><p className="mt-1 text-xs text-slate-500">Available for selection</p></div></div>
        </article>
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-700"><XCircle className="h-5 w-5" aria-hidden="true" /></span><div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Inactive plans</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : plans.filter(plan => !plan.isActive).length.toLocaleString()}</p><p className="mt-1 text-xs text-slate-500">Unavailable for new purchases</p></div></div>
        </article>
      </div>

      {/* Plans Table */}
      <section className="admin-table-shell" aria-labelledby="payment-plan-directory-title">
        <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><WalletCards className="h-[18px] w-[18px]" aria-hidden="true" /></span>
            <div><h2 id="payment-plan-directory-title" className="text-base font-semibold text-slate-950">Plan directory</h2><p className="mt-0.5 text-xs text-slate-500" aria-live="polite">{loading ? 'Loading plans…' : `${plans.length} ${plans.length === 1 ? 'plan' : 'plans'} shown`}</p></div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={fetchPlans} disabled={loading} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"><RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh</button>
            <button type="button" onClick={() => setAreFiltersOpen(current => !current)} aria-expanded={areFiltersOpen} aria-controls="payment-plan-filters" className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areFiltersOpen || activeFilterCount > 0 ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}><SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />Filters{activeFilterCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">{activeFilterCount}</span>}<ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" /></button>
          </div>
        </div>

        <div id="payment-plan-filters" hidden={!areFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <label htmlFor="payment-plan-active-only" className="flex max-w-xl cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50/40">
              <input id="payment-plan-active-only" type="checkbox" checked={activeOnly} onChange={event => setActiveOnly(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              <span><span className="block text-sm font-semibold text-slate-900">Show active plans only</span><span className="mt-1 block text-xs leading-5 text-slate-500">Hide inactive plans from the directory while keeping them unchanged.</span></span>
            </label>
            {activeFilterCount > 0 && <button type="button" onClick={() => setActiveOnly(false)} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"><XCircle className="mr-2 h-4 w-4" aria-hidden="true" />Clear filter</button>}
          </div>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-14"><LoaderCircle className="mb-3 h-8 w-8 animate-spin text-blue-600" aria-hidden="true" /><h3 className="text-sm font-semibold text-slate-900">Loading payment plans…</h3><p className="mt-1 text-sm text-slate-500">Fetching the latest subscription catalog.</p></div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-4 border-b border-red-200 bg-red-50 px-4 py-4 sm:px-5" role="alert">
            <div className="flex items-start gap-3"><CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" /><div><p className="text-sm font-semibold text-red-900">Unable to load payment plans</p><p className="mt-1 text-sm text-red-700">{error}</p></div></div>
            <button type="button" onClick={fetchPlans} className="shrink-0 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2">Try again</button>
          </div>
        )}

        {!loading && !error && (
          <>
            {plans.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-6 py-14 text-center"><span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><FileText className="h-5 w-5" aria-hidden="true" /></span><h3 className="text-sm font-semibold text-slate-900">No plans found</h3><p className="mt-1 text-sm text-slate-500">{activeOnly ? 'No active plans are currently available.' : 'Create the first plan to build the subscription catalog.'}</p>{!activeOnly && <button type="button" onClick={() => { setError(null); setIsCreateModalOpen(true); }} className="mt-4 inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"><Plus className="mr-2 h-4 w-4" aria-hidden="true" />Create plan</button>}</div>
            ) : (
              <div className="admin-table-scroll" role="region" aria-label="Payment plan data table" tabIndex={0}>
                <table className="admin-data-table min-w-max">
                  <thead><tr><th scope="col">Status</th><th scope="col">Plan name</th><th scope="col">Amount</th><th scope="col">Validity</th><th scope="col">Description</th><th scope="col">Features</th><th scope="col">Created</th><th scope="col">Updated</th><th scope="col">Actions</th></tr></thead>
                  <tbody>
                    {plans.map((plan) => (
                      <tr key={plan.planId} className="transition-colors hover:bg-blue-50/40">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                              plan.isActive
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                : 'border-red-200 bg-red-50 text-red-700'
                            }`}
                          >
                            {plan.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{plan.planName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatCurrency(plan.amount, plan.currency || defaultCurrency)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatValidity(plan.validity)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate" title={plan.description || 'N/A'}>
                            {plan.description || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {(plan.features || []).length > 0 ? (
                            <ul className="min-w-[180px] list-disc pl-4 text-sm text-gray-700">
                              {(plan.features || []).map((feature, index) => <li key={`${plan.planId}-${index}`}>{feature}</li>)}
                            </ul>
                          ) : (
                            <span className="text-sm text-gray-500">None</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateString(plan.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateString(plan.updatedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleEditClick(plan)}
                            type="button"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            title="Edit plan"
                            aria-label={`Edit ${plan.planName}`}
                          >
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* Create Plan Modal */}
      <CreatePlanModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreatePlan}
        loading={isCreating}
      />

      {/* Edit Plan Modal */}
      <EditPlanModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPlan(null);
        }}
        onSubmit={handleUpdatePlan}
        plan={selectedPlan}
        loading={isUpdating}
      />
    </div>
  );
};
